import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, ViewChild
} from '@angular/core';
import { Router } from '@angular/router';

import { CementingStatusService } from '../../Services/cementing-status.service';
import { EventService } from '../../Services/event.service';
import {
  attemptDate, CEMENTING_LOOP_VIDEO_URL, CementingAttempt, CementingCategory, CementingDifficulty, CementingMoment, CementingMomentKind, CementingStatus,
  ChartColumn, ChartView, deriveChart, derivePlan, deriveTable, findCategory, moduleName, PillIcon, PlanGroup, PlanTile, TableRow
} from '../../models/cementing-status';
import {
  ActionName, ActionView, CEMENTING_CHAT_BLOCKED, CEMENTING_MENTOR_URL, CEMENTING_SUPPORT_EMAIL, deriveAction, deriveHardNotice, deriveLevelCard, HardNotice, LevelCardView, LocalChoice
} from '../../models/cementing-messages';

type PanelView = 'plan' | 'chart' | 'table';

/**
 * Panel size, from the panel's own width (design boards): the Modules view
 * panel (680px) is `normal`, the Journey view panel (1066px / 866px) `wide`
 * and the 992px-screen Modules view panel (548px) `compact`.
 */
export type PanelSize = 'compact' | 'normal' | 'wide';

/** Level ring geometry per panel size (design boards FMS / FJS / NMS). */
export interface RingGeometry {
  size: number;
  c: number;
  r: number;
  stroke: number;
  labelY: number;
  labelSize: number;
  valueY: number;
  valueSize: number;
}

const RINGS: { [size in PanelSize]: RingGeometry } = {
  compact: { size: 92, c: 46, r: 36, stroke: 6, labelY: 39, labelSize: 9, valueY: 59, valueSize: 22 },
  normal: { size: 104, c: 52, r: 40, stroke: 6, labelY: 45, labelSize: 9, valueY: 65, valueSize: 24 },
  wide: { size: 112, c: 56, r: 44, stroke: 7, labelY: 47, labelSize: 10, valueY: 71, valueSize: 26 }
};


/** The largest the dial grows to when the action box beside it is tall. */
const RING_MAX = 150;

/** Dial geometry at any size, in the proportions of the 112px Journey dial. */
function ringGeometry(size: number): RingGeometry {
  const c = size / 2;
  return {
    size, c, r: +(size * 0.393).toFixed(1), stroke: Math.max(6, Math.round(size * 0.0625)),
    labelY: Math.round(c - size * 0.08), labelSize: Math.max(9, Math.round(size * 0.09)),
    valueY: Math.round(c + size * 0.134), valueSize: Math.round(size * 0.232)
  };
}

/** Default chart width per panel size when the host has not been laid out yet. */
const CHART_WIDTH: { [size in PanelSize]: number } = { compact: 514, normal: 646, wide: 1032 };

/** Catalog labels carry a leading glyph; the boards draw it as the pill icon instead. */
const GLYPH_ICONS: { [glyph: string]: PillIcon } = {
  '\u2193': 'down', '\u2197': 'up-right', '\u2713': 'check', '\u2605': 'star', '\u2691': 'flag', '\u21bb': 'redo', '\u25c6': 'check', '\u24d8': 'info'
};

export type TableSortKey = 'quiz' | 'date' | 'score' | 'mistakes' | 'l20' | 'happened' | 'action';

const TABLE_COLUMNS: { key: TableSortKey; label: string }[] = [
  { key: 'quiz', label: 'Quiz' }, { key: 'date', label: 'Date' }, { key: 'score', label: 'Score' }, { key: 'mistakes', label: 'Mistakes' },
  { key: 'l20', label: 'L20' }, { key: 'happened', label: 'What happened' }, { key: 'action', label: 'Action' }
];

/** Moments the action card speaks for (L1); silent, practice and abandoned rows show no card. */
const CARD_MOMENTS: CementingMomentKind[] = [
  'WARMUP_DONE', 'FIRST_CHECKIN_NEXT', 'CELEBRATE_THRESHOLD', 'CELEBRATE_MASTERY', 'CHECKIN_1', 'CHECKIN_BELOW_AGAIN', 'CHECKIN_MASTERY', 'FINAL'
];

/** localStorage key of the moments already reported as shown (quizId:moment), newest last. */
const SHOWN_MOMENTS_KEY = 'cementingMomentsShown';
const SHOWN_MOMENTS_MAX = 200;
/** How long "Contact my mentor" waits for Intercom to report its messenger open before falling back to mail. */
const CHAT_OPEN_WAIT_MS = 2000;

interface QuizMenu {
  quizNumber: number;
  attempt: CementingAttempt;
  /** Where it was opened from, for positioning. */
  source: 'plan' | 'table';
  /** Plan only: viewport position of the tile's top-left corner (the menu sits under it). */
  x?: number;
  y?: number;
}

interface ChartLayout {
  width: number;
  plotLeft: number;
  plotRight: number;
  plotTop: number;
  plotBottom: number;
  columnWidth: number;
  barWidth: number;
  height: number;
  thresholdY: number;
  masteryY: number;
  ticks: { percent: number; y: number }[];
  /** Y of the axis-break glyphs: the two compressed segments of the scale. */
  breaks: number[];
}

interface ChartBar {
  column: ChartColumn;
  index: number;
  x: number;
  cx: number;
  y: number;
  height: number;
}

/**
 * The cementing module panel of one stage (Medium or Hard): level card,
 * action card (next quiz, check-in choices, done states) and the progress
 * card with its Plan / Chart / Table views. Driven by one `CementingCategory`
 * of the status API; the only call made here is the choice on a waiting moment.
 */
@Component({
  selector: 'app-cementing-panel',
  templateUrl: './cementing-panel.component.html',
  styleUrls: ['./cementing-panel.component.scss']
})
export class CementingPanelComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() category: CementingCategory;
  /** The whole subject status: the Hard panel reads Medium's state for its warning. */
  @Input() status: CementingStatus | null = null;
  /** Subject short name, e.g. CR, SC, GI-TA. */
  @Input() subject: string;
  /** VERBAL | QUANT | DI, used for the Scholaranium URLs. */
  @Input() section: string;
  /** Course page name for the in-course quiz route (module view /page/:page, journey view /journey/page/:page). */
  @Input() pageName: string;
  /** "Back to course" on a finished module: the host collapses the module. */
  @Output() backToCourse = new EventEmitter<void>();
  /** "Go to Medium Cementing" / "Move to Hard Cementing": the host opens that module. */
  @Output() openModule = new EventEmitter<CementingDifficulty>();

  @ViewChild('chartHost') chartHost: ElementRef<HTMLDivElement>;
  @ViewChild('topRow') topRow: ElementRef<HTMLDivElement>;
  @ViewChild('actionCard') actionCard: ElementRef<HTMLDivElement>;
  @ViewChild('levelPill') levelPill: ElementRef<HTMLElement>;
  @ViewChild('levelLink') levelLink: ElementRef<HTMLElement>;
  @ViewChild('planHost') planHost: ElementRef<HTMLDivElement>;

  name = '';
  level: LevelCardView | null = null;
  action: ActionView | null = null;
  plan: PlanGroup[] = [];
  chart: ChartView | null = null;
  table: TableRow[] = [];
  /** `table` in the order the Table view shows it (see `sortBy`). */
  sortedTable: TableRow[] = [];
  hardNotice: HardNotice | null = null;

  size: PanelSize = 'normal';
  readonly columns = TABLE_COLUMNS;
  sortKey: TableSortKey = 'date';
  sortDir: 'asc' | 'desc' = 'asc';

  view: PanelView = 'plan';
  levelInfoOpen = false;
  /** Dial size that fills the level card when the action box beside it is tall; null = the panel size's own dial. */
  ringFill: number | null = null;
  helpOpen = false;
  legendOpen = false;
  menu: QuizMenu | null = null;
  tableMenu: number | null = null;
  /**
   * Viewport position of the open table menu: the Action button's right edge and top, or its bottom
   * edge when the menu opens upwards (no room below). The table scrolls sideways and would clip it.
   */
  tableMenuPos: { x: number; y: number; up: boolean } | null = null;
  tablePair: number[] | null = null;
  tableBanner: { pair: string; l20: string } | null = null;
  hover: ChartBar | null = null;
  tooltipLeft = 0;
  tooltipRight = false;
  /** The chart is wider than its host: scroll it instead of letting the tooltip overflow. */
  chartScroll = false;
  busy = false;
  error: string | null = null;
  /** "Contact my mentor": whether Intercom reported its messenger open since the last click. */
  private chatShown = false;
  private chatListening = false;
  private chatTimer: any = null;

  // chart geometry (px inside the chart host)
  layout: ChartLayout | null = null;
  bars: ChartBar[] = [];
  readonly warmupNote = "Warm-up: doesn't count. ";

  private localChoice: LocalChoice = null;
  /** The moment the action card speaks for, and when it first appeared (Seconds_On_Card). */
  private shownMoment: CementingMoment | null = null;
  private shownAt = 0;
  /** Scroll the plan row to the current quiz on the next layout (after a new status or switching to Plan). */
  private planScrollPending = true;
  private layoutTimer: any = null;
  /** Re-measures the panel when its host is laid out or resized (a collapsed journey level has no width yet). */
  private resizeObserver: { observe(el: Element): void; disconnect(): void } | null = null;

  constructor(private cementingService: CementingStatusService, private router: Router, private cdr: ChangeDetectorRef, private host: ElementRef<HTMLElement>,
    private events: EventService) {}

  ngOnChanges() {
    if (!this.category) {
      this.level = null;
      this.action = null;
      return;
    }
    this.name = moduleName(this.category);
    this.localChoice = this.restoreChoice();
    this.derive();
    this.scheduleLayout();
  }

  ngAfterViewInit() {
    this.scheduleLayout();
    const RO = (window as any).ResizeObserver;
    if (RO && this.host && this.host.nativeElement) {
      this.resizeObserver = new RO(() => this.scheduleLayout());
      this.resizeObserver.observe(this.host.nativeElement);
    }
  }

  ngOnDestroy() {
    if (this.layoutTimer) { clearTimeout(this.layoutTimer); }
    if (this.chatTimer) { clearTimeout(this.chatTimer); }
    if (this.resizeObserver) { this.resizeObserver.disconnect(); this.resizeObserver = null; }
  }

  @HostListener('window:resize')
  onResize() {
    this.scheduleLayout();
  }

  /** The plan and table menus are anchored to the viewport: close them when the page scrolls. */
  @HostListener('window:scroll')
  onWindowScroll() {
    if (this.tableMenu !== null) {
      this.tableMenu = null;
      this.cdr.markForCheck();
    }
    if (this.menu && this.menu.source === 'plan') {
      this.menu = null;
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:click')
  onDocumentClick() {
    // Menus close on any click outside; clicks inside stop propagation in the template.
    if (this.menu || this.tableMenu !== null) {
      this.menu = null;
      this.tableMenu = null;
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.menu = null;
    this.tableMenu = null;
    this.helpOpen = false;
    this.hover = null;
  }

  /* ----------------------------------------------------------------- table */

  /** Column header click: sort by that column, a second click flips the direction. */
  sortBy(key: TableSortKey) {
    if (key === 'action') { return; }
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
    this.tableMenu = null;
    this.applySort();
  }

  sortIcon(key: TableSortKey): string {
    if (key !== this.sortKey) { return 'sort'; }
    return this.sortDir === 'asc' ? 'sort-up' : 'sort-down';
  }

  /** Date ascending is the derived order (done / next rows in place); any other sort moves those rows to the end. */
  private applySort() {
    if (this.sortKey === 'date' && this.sortDir === 'asc') {
      this.sortedTable = this.table;
      return;
    }
    const value = (row: TableRow): number | string => {
      const a = row.attempt;
      switch (this.sortKey) {
        case 'quiz': return (row.quizNumber || 0) + (row.kind === 'retake' ? 0.5 : 0);
        case 'date': { const d = a ? attemptDate(a) : null; return d ? d.getTime() : 0; }
        case 'score': return a && a.accuracyPercent !== null ? a.accuracyPercent : -1;
        case 'mistakes': return a && a.mistakes !== null ? a.mistakes : -1;
        case 'l20': return row.l20 ? parseInt(row.l20.percent, 10) : -1;
        default: return row.happened ? row.happened.text.toLowerCase() : '';
      }
    };
    const dir = this.sortDir === 'asc' ? 1 : -1;
    const main = this.table.filter(r => r.kind === 'attempt' || r.kind === 'retake').map((row, index) => ({ row, index }));
    main.sort((x, y) => {
      const a = value(x.row); const b = value(y.row);
      const cmp = a < b ? -1 : a > b ? 1 : 0;
      return cmp !== 0 ? cmp * dir : x.index - y.index;
    });
    this.sortedTable = main.map(m => m.row).concat(this.table.filter(r => r.kind !== 'attempt' && r.kind !== 'retake'));
  }

  /* ---------------------------------------------------------------- derive */

  private derive() {
    const c = this.category;
    this.level = deriveLevelCard(c);
    const sibling = findCategory(this.status, c.difficulty === 'HARD' ? 'MEDIUM' : 'HARD');
    this.action = deriveAction(c, { localChoice: this.localChoice, sibling });
    this.plan = derivePlan(c);
    this.chart = deriveChart(c);
    this.table = deriveTable(c, this.tablePair);
    this.applySort();
    this.hardNotice = c.difficulty === 'HARD' ? deriveHardNotice(c, sibling) : null;
    this.menu = null;
    this.tableMenu = null;
    this.hover = null;
    this.planScrollPending = true;
    this.trackMomentShown();
  }

  /** Level ring geometry for the current panel size (smaller while the L20 box is open). */
  get ring(): RingGeometry {
    return this.ringFill ? ringGeometry(this.ringFill) : RINGS[this.size];
  }

  /** Height of the threshold -> mastery track beside the dial: it follows a grown dial. */
  get barsHeight(): number | null {
    return this.ringFill ? Math.round(this.ringFill * 0.82) : null;
  }

  /**
   * The level card is as tall as the action box beside it, whose height depends on its message.
   * The dial grows into that height (up to RING_MAX) instead of floating in empty space. It is sized
   * from the action box's own content height, not the row height, so the dial never feeds back
   * into the row it sits in. Side by side only: stacked (narrow screens) the card keeps its size.
   */
  private measureRing() {
    const action = this.actionCard ? this.actionCard.nativeElement : null;
    const row = this.topRow ? this.topRow.nativeElement : null;
    const pill = this.levelPill ? this.levelPill.nativeElement : null;
    const link = this.levelLink ? this.levelLink.nativeElement : null;
    const base = RINGS[this.size].size;
    // "What is L20?" open: the box opens below the dial and the dial keeps the size it had
    if (this.levelInfoOpen) { return; }
    if (!action || !row || !pill || !link) { this.ringFill = null; return; }
    const sideBySide = getComputedStyle(row).gridTemplateColumns.split(' ').filter(x => !!x).length > 1;
    if (!sideBySide) { this.ringFill = null; return; }
    const style = getComputedStyle(action);
    const kids = Array.from(action.children) as HTMLElement[];
    const gap = parseFloat(style.rowGap || style.gap || '0') || 0;
    const natural = kids.reduce((sum, k) => sum + k.offsetHeight, 0) + gap * Math.max(0, kids.length - 1)
      + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    // level card: 14px padding top and bottom, 1px borders, two 10px gaps around the dial row
    const available = natural - (28 + 2 + 20) - pill.offsetHeight - link.offsetHeight;
    const size = Math.min(RING_MAX, Math.floor(available));
    this.ringFill = size > base ? size : null;
  }

  /**
   * Fill of the threshold -> mastery track beside the ring: 0% at the threshold,
   * 100% at mastery, and a short stub while the L20 is below the threshold.
   */
  levelFill(): number {
    const l = this.level;
    if (!l || l.percent === null) { return 0; }
    if (l.percent < l.thresholdPercent) { return 8; }
    const span = l.masteryPercent - l.thresholdPercent;
    return span <= 0 ? 100 : Math.min(100, Math.round((l.percent - l.thresholdPercent) / span * 100));
  }

  /** Pill text without the catalog's leading glyph (the glyph becomes the icon). */
  pillLabel(label: string): string {
    const first = label ? label.charAt(0) : '';
    return GLYPH_ICONS[first] ? label.slice(1).replace(/^\s+/, '') : label;
  }

  /** Icon of a pill: the model's icon, else the one its leading glyph stands for. */
  pillIcon(pill: { label: string; icon: PillIcon }): PillIcon {
    if (pill.icon && pill.icon !== 'none') { return pill.icon; }
    const first = pill.label ? pill.label.charAt(0) : '';
    return GLYPH_ICONS[first] || 'none';
  }

  readonly loopUrl = CEMENTING_LOOP_VIDEO_URL;

  /**
   * A Loop step starts with "Watch the Loop video": that phrase becomes the link to the video,
   * the rest of the sentence stays plain text. Null when the step does not start with it.
   */
  loopLinkParts(text: string): { link: string; rest: string } | null {
    const phrase = 'Watch the Loop video';
    return this.loopUrl && text && text.indexOf(phrase) === 0 ? { link: phrase, rest: text.slice(phrase.length) } : null;
  }

  /** Reattempts exist on the fixed path only: legacy (dynamic-pool) quizzes have no template to start again. */
  get reattemptAllowed(): boolean { return !!this.status && this.status.mode === 'FIXED'; }

  /** Splits "Aim for {{3 mistakes or fewer}}." into plain / bold parts for the template. */
  textParts(text: string | null): { text: string; strong: boolean }[] {
    if (!text) { return []; }
    return text.split(/(\{\{[^}]*\}\})/).filter(p => p !== '').map(p =>
      p.indexOf('{{') === 0 ? { text: p.slice(2, -2), strong: true } : { text: p, strong: false });
  }

  /* --------------------------------------------------------------- actions */

  setView(view: PanelView) {
    this.view = view;
    this.menu = null;
    this.tableMenu = null;
    this.hover = null;
    if (view !== 'table') { this.clearTablePair(); }
    if (view === 'plan') { this.planScrollPending = true; }
    this.scheduleLayout();
  }

  onPrimary() {
    if (!this.action || !this.action.primary) { return; }
    this.run(this.action.primary.action);
  }

  onSecondary() {
    if (!this.action || !this.action.secondary) { return; }
    this.run(this.action.secondary.action);
  }

  run(action: ActionName) {
    this.error = null;
    this.trackButton(action);
    switch (action) {
      case 'start':
      case 'start-practice':
      case 'confirm-start':
        if (this.localChoice === 'CONFIRM_START') { this.localChoice = null; }
        this.startQuiz();
        break;
      case 'go-back':
        this.localChoice = null;
        this.derive();
        break;
      case 'review-last':
        this.review(this.action && this.action.reviewAttempt);
        break;
      case 'contact-mentor':
        this.contactMentor();
        break;
      case 'open-medium':
        this.openModule.emit('MEDIUM');
        break;
      case 'open-hard':
        this.openModule.emit('HARD');
        break;
      case 'keep':
        // Shown at once, recorded on the server; it stays changeable to "Finish here" until the next quiz starts.
        this.localChoice = 'KEEP_GOING';
        this.derive();
        this.keepGoing();
        break;
      case 'stop':
        this.localChoice = 'STOP';
        this.derive();
        break;
      case 'back':
      case 'change':
        this.localChoice = null;
        this.derive();
        break;
      case 'finish':
        this.finish();
        break;
      case 'help':
        this.helpOpen = true;
        break;
      case 'back-to-course':
        this.backToCourse.emit();
        break;
    }
  }

  /** Records "Keep going" on the server. On failure the card stays: the server infers it when the next quiz starts. */
  private keepGoing() {
    if (this.busy) { return; }
    this.busy = true;
    this.cementingService.recordChoice(this.subject, this.category.correlationId, 'KEEP_GOING').subscribe(status => {
      this.busy = false;
      const fresh = status ? findCategory(status, this.category.difficulty) : null;
      if (!fresh) {
        console.warn('[cementing-panel] could not record "Keep going"; the server infers it when the next quiz starts');
        return;
      }
      // The host receives the new status through the service; keep the panel coherent meanwhile.
      this.category = fresh;
      this.status = status;
      this.localChoice = this.restoreChoice();
      this.derive();
      this.scheduleLayout();
    });
  }

  private finish() {
    if (this.busy) { return; }
    this.busy = true;
    this.cementingService.recordChoice(this.subject, this.category.correlationId, 'STOP').subscribe(status => {
      this.busy = false;
      this.localChoice = null;
      if (!status) {
        this.error = 'We could not record your choice. Please try again.';
        this.derive();
        return;
      }
      // The host receives the new status through the service; keep the panel coherent meanwhile.
      const fresh = findCategory(status, this.category.difficulty);
      if (fresh) {
        this.category = fresh;
        this.status = status;
        this.derive();
        this.scheduleLayout();
      }
    });
  }

  /** Opens the next quiz of the module in the course player (Scholaranium framed in embedded mode). */
  startQuiz() {
    if (!this.category || !this.category.nextQuiz) { return; }
    this.navigateInCourse(['cementing', this.section, this.subject, this.category.difficulty]);
  }

  /**
   * Start from the Plan or the Table while a choice with "Finish here" is still waiting:
   * the server would read it as "keep going", so the card asks first (catalog X4).
   */
  startFromList() {
    const waiting = this.action && this.action.moment;
    if (waiting && waiting.allowsStop && !waiting.choice && this.localChoice !== 'KEEP_GOING') {
      this.localChoice = 'CONFIRM_START';
      this.derive();
      return;
    }
    this.startQuiz();
  }

  /**
   * "Contact my mentor": the configured link, else the in-app chat, else support mail (spec open item 6).
   * Neither a function nor `booted` proves the chat can open: index.html always installs Intercom's queueing
   * stub, and a booted widget can still be refused by Intercom (409 when several Intercom users share the
   * email). So the chat counts as open only when Intercom's onShow fires; otherwise say so and open mail.
   */
  contactMentor() {
    if (CEMENTING_MENTOR_URL) { window.open(CEMENTING_MENTOR_URL, '_blank'); return; }
    const intercom = (window as any).Intercom;
    if (typeof intercom !== 'function' || !intercom.booted) { this.mailMentor(); return; }
    if (!this.chatListening) {
      this.chatListening = true;
      intercom('onShow', () => { this.chatShown = true; });
    }
    this.chatShown = false;
    intercom('show');
    if (this.chatTimer) { clearTimeout(this.chatTimer); }
    // Within Chrome's user-activation window, so the mailto launch is still allowed.
    this.chatTimer = setTimeout(() => {
      this.chatTimer = null;
      if (!this.chatShown) { this.mailMentor(); this.cdr.markForCheck(); }
    }, CHAT_OPEN_WAIT_MS);
  }

  private mailMentor() {
    this.error = CEMENTING_CHAT_BLOCKED;
    window.location.href = 'mailto:' + CEMENTING_SUPPORT_EMAIL + '?subject=' + encodeURIComponent('Cementing: please connect me with my mentor');
  }

  /** Review-only retake of a finished quiz, named to Scholaranium by its template correlation id. */
  reattempt(attempt: CementingAttempt) {
    if (!attempt || !attempt.correlationId || !this.reattemptAllowed) { return; }
    this.navigateInCourse(['cementing', this.section, this.subject, this.category.difficulty], { template: attempt.correlationId, retake: 1 });
  }

  review(attempt: CementingAttempt | null) {
    if (!attempt || !attempt.quizId) { return; }
    if (attempt.status === 'INPROGRESS') { this.startQuiz(); return; }
    // An exited quiz can be reviewed too: its answers so far (design board FMV12).
    if (attempt.status !== 'COMPLETED' && attempt.status !== 'ABANDONED') { return; }
    this.navigateInCourse(['cementing-review', this.section, attempt.quizId]);
  }

  /** Module view lives under /page/:page, journey view under /journey/page/:page. */
  private navigateInCourse(tail: any[], queryParams?: { [key: string]: any }) {
    const page = this.pageName || this.pageNameFromUrl();
    if (!page) {
      console.error('cementing-panel: no page name; cannot open', tail);
      return;
    }
    const extras = queryParams ? { queryParams } : undefined;
    if (window.location.href.indexOf('/journey') > -1) {
      this.router.navigate(['/journey', 'page', page, ...tail], extras);
    } else {
      this.router.navigate(['/page', page, ...tail], extras);
    }
  }

  private pageNameFromUrl(): string | null {
    const match = /\/page\/([^\/?#]+)/.exec(window.location.pathname);
    return match ? decodeURIComponent(match[1]) : null;
  }

  /* ------------------------------------------------------------ quiz menus */

  onTile(tile: PlanTile, event: Event) {
    event.stopPropagation();
    this.tableMenu = null;
    if (tile.kind === 'next') { this.startFromList(); return; }
    if (tile.kind === 'inprogress') { this.startQuiz(); return; }
    if ((tile.kind !== 'done' && tile.kind !== 'abandoned') || !tile.attempt) { this.menu = null; return; }
    if (this.menu && this.menu.quizNumber === tile.quizNumber && this.menu.source === 'plan') { this.menu = null; return; }
    const target = event.currentTarget as HTMLElement;
    const rect = target && target.getBoundingClientRect ? target.getBoundingClientRect() : null;
    this.menu = { quizNumber: tile.quizNumber, attempt: tile.attempt, source: 'plan', x: rect ? rect.left : 0, y: rect ? rect.top : 0 };
  }

  tileTitle(tile: PlanTile): string {
    switch (tile.kind) {
      case 'done': return 'Quiz ' + tile.quizNumber + ' · ' + tile.score + ' · click to review' + (this.reattemptAllowed ? ' or reattempt' : '');
      case 'abandoned': return 'Quiz ' + tile.quizNumber + ' · exited before the end, doesn\'t count · click to review';
      case 'inprogress': return 'Quiz ' + tile.quizNumber + ' · in progress' + (tile.progress ? ' (' + tile.progress.answered + ' of ' + tile.progress.total + ' answered)' : '') + ', click to resume';
      case 'next': return 'Quiz ' + tile.quizNumber + ' · next quiz, click to start';
      case 'extra': return 'Quiz ' + tile.quizNumber + ' · beyond the planned module';
      default: return 'Quiz ' + tile.quizNumber;
    }
  }

  onTableAction(row: TableRow, event: Event) {
    event.stopPropagation();
    this.menu = null;
    const key = row.kind === 'retake' ? -(row.quizNumber || 0) : (row.quizNumber || 0);
    if (this.tableMenu === key) { this.tableMenu = null; return; }
    const button = event.currentTarget as HTMLElement;
    const rect = button && button.getBoundingClientRect ? button.getBoundingClientRect() : null;
    // About 150px for the menu (title, note on an exited quiz, two items): open upwards when it would leave the window.
    const up = !!rect && rect.bottom + 4 + 150 > window.innerHeight && rect.top > 150;
    this.tableMenuPos = rect ? { x: rect.right, y: up ? rect.top : rect.bottom, up } : null;
    this.tableMenu = key;
  }

  tableMenuKey(row: TableRow): number {
    return row.kind === 'retake' ? -(row.quizNumber || 0) : (row.quizNumber || 0);
  }

  closeMenus() {
    this.menu = null;
    this.tableMenu = null;
  }

  /** Opens the "What is L20?" box: the row switches to 60/40 at the height it had, so nothing below moves. */
  /** Opens the "What is L20?" box below the dial (the dial keeps its size; the row grows while it is open). */
  toggleLevelInfo(event: Event) {
    event.stopPropagation();
    this.levelInfoOpen = !this.levelInfoOpen;
    if (!this.levelInfoOpen) { this.scheduleLayout(); }
  }

  /* ----------------------------------------------------------------- chart */

  private scheduleLayout() {
    if (this.layoutTimer) { clearTimeout(this.layoutTimer); }
    this.layoutTimer = setTimeout(() => {
      this.layoutTimer = null;
      this.layoutChart();
    }, 0);
  }

  /** Panel size from the panel's own width: journey (>= 800px) is wide, a 992px-screen module (< 600px) compact. */
  private measure() {
    const el = this.host && this.host.nativeElement;
    const width = el && el.clientWidth ? el.clientWidth : 0;
    this.size = !width ? 'normal' : width >= 800 ? 'wide' : width < 600 ? 'compact' : 'normal';
  }

  /** Recomputes the panel size and the chart geometry from the current host width (also used by tests). */
  layoutChart() {
    this.measure();
    this.measureRing();
    if (!this.chart) { this.cdr.detectChanges(); this.scrollPlanToCurrent(); return; }
    const host = this.chartHost ? this.chartHost.nativeElement : null;
    const available = host && host.clientWidth ? host.clientWidth : CHART_WIDTH[this.size];
    const plotLeft = 42;
    const rightPad = 86;
    const n = Math.max(1, this.chart.columns.length);
    const columnWidth = Math.max(52, (available - plotLeft - rightPad) / n);
    const width = Math.max(available, plotLeft + columnWidth * n + rightPad);
    const plotTop = 30;
    const plotBottom = 232;
    const yOf = this.yScale(plotTop, plotBottom);
    const t = this.chart.thresholdPercent;
    const m = this.chart.masteryPercent;
    const tickValues = [40, t - 10];
    for (let p = t - 5; p <= m; p += 5) { tickValues.push(p); }
    tickValues.push(100);
    const ticks = tickValues.filter((p, i) => p >= 40 && p <= 100 && tickValues.indexOf(p) === i).map(p => ({ percent: p, y: yOf(p) }));
    this.layout = {
      width, plotLeft, plotRight: plotLeft + columnWidth * n, plotTop, plotBottom, columnWidth,
      barWidth: Math.min(30, Math.max(18, columnWidth - 22)), height: 280,
      thresholdY: yOf(t), masteryY: yOf(m), ticks,
      // the top break marks the compressed mastery -> 100 stretch; there is none when mastery = threshold
      breaks: m > t ? [(yOf(40) + yOf(t - 10)) / 2 + 3, (yOf(m) + yOf(100)) / 2 + 3] : [(yOf(40) + yOf(t - 10)) / 2 + 3]
    };
    this.chartScroll = width > available;
    this.bars = this.chart.columns.map((column, index) => {
      const x = plotLeft + index * columnWidth;
      const cx = x + columnWidth / 2;
      const y = column.l20Percent === null ? plotBottom : yOf(column.l20Percent);
      return { column, index, x, cx, y, height: plotBottom - y };
    });
    this.cdr.detectChanges();
    this.scrollPlanToCurrent();
  }

  /**
   * The plan is one line that scrolls sideways: bring the next quiz (or the last one) into view,
   * once per new status or switch to Plan, so a manual scroll is not undone on resize.
   */
  private scrollPlanToCurrent() {
    if (!this.planScrollPending || this.view !== 'plan') { return; }
    const row = this.planHost ? this.planHost.nativeElement : null;
    if (!row || !row.clientWidth) { return; }
    this.planScrollPending = false;
    if (row.scrollWidth <= row.clientWidth) { return; }
    const tiles = row.querySelectorAll('.cq-tile');
    const target = (row.querySelector('.cq-tile--next') || row.querySelector('.cq-tile--inprogress') || tiles[tiles.length - 1]) as HTMLElement;
    if (!target) { return; }
    const rowLeft = row.getBoundingClientRect().left;
    const right = target.getBoundingClientRect().right - rowLeft + row.scrollLeft;
    row.scrollLeft = Math.max(0, right + 24 - row.clientWidth);
  }

  /**
   * Y scale of the chart (design "Option B"): the axis runs 40–100% but the goal zone is stretched.
   * 40 → threshold-10 takes 10% of the height, the 10 points under the threshold 20%, threshold → mastery 58%, mastery → 100 12%.
   */
  private yScale(plotTop: number, plotBottom: number): (percent: number) => number {
    const t = this.chart ? this.chart.thresholdPercent : 70;
    const m = this.chart ? this.chart.masteryPercent : 90;
    const stops = [40, Math.max(40, t - 10), t, Math.max(t, m), 100];
    // Mastery at the threshold (no goal zone between them): the zone above the threshold takes
    // what the threshold -> mastery stretch would have had, so the plot still fills its height.
    const shares = m > t ? [0.10, 0.20, 0.58, 0.12] : [0.10, 0.20, 0, 0.70];
    const h = plotBottom - plotTop;
    return (percent: number) => {
      const p = Math.min(100, Math.max(40, percent));
      let y = plotBottom;
      for (let i = 0; i < 4; i++) {
        const a = stops[i]; const b = stops[i + 1];
        if (b <= a) { continue; }
        if (p <= a) { break; }
        const f = Math.min(1, (p - a) / (b - a));
        y -= f * shares[i] * h;
        if (p <= b) { break; }
      }
      return +y.toFixed(2);
    };
  }

  /** The L20 pop-over sits 40px right of the hovered bar, or to its left when it would run past the chart. */
  showTip(bar: ChartBar) {
    if (bar.column.future || !bar.column.attempt) { this.hover = null; return; }
    this.hover = bar;
    const tipWidth = this.size === 'compact' ? 280 : 316;
    const width = this.layout ? this.layout.width : CHART_WIDTH[this.size];
    this.tooltipRight = bar.cx + 40 + tipWidth > width + 16;
    this.tooltipLeft = this.tooltipRight ? bar.cx - tipWidth - 8 : bar.cx + 40;
    if (this.chartScroll) { this.tooltipLeft = Math.max(0, this.tooltipLeft); }
  }

  hideTip() {
    this.hover = null;
  }

  /** From the L20 tooltip: show the two quizzes behind it in the table. */
  showPairInTable(column: ChartColumn, event?: Event) {
    if (event) { event.stopPropagation(); }
    if (!column.l20) { return; }
    this.tablePair = [column.l20.prevQuizNumber, column.l20.quizNumber];
    this.tableBanner = {
      pair: 'Quiz ' + column.l20.prevQuizNumber + ' and Quiz ' + column.l20.quizNumber,
      l20: 'L20 after Quiz ' + column.l20.quizNumber + ' (' + column.l20.percent + '%)'
    };
    this.table = deriveTable(this.category, this.tablePair);
    this.applySort();
    this.hover = null;
    this.view = 'table';
  }

  clearTablePair() {
    if (!this.tablePair) { return; }
    this.tablePair = null;
    this.tableBanner = null;
    this.table = deriveTable(this.category, null);
    this.applySort();
  }

  backToChart() {
    this.clearTablePair();
    this.setView('chart');
  }

  /** Dot at the end of the level ring. */
  ringDot(percent: number | null): { x: number; y: number } {
    const p = percent === null ? 0 : Math.min(100, Math.max(0, percent));
    const angle = p / 100 * 2 * Math.PI;
    const g = this.ring;
    return { x: +(g.c + g.r * Math.sin(angle)).toFixed(1), y: +(g.c - g.r * Math.cos(angle)).toFixed(1) };
  }

  ringDash(percent: number | null): string {
    const p = percent === null ? 0 : Math.min(100, Math.max(0, percent));
    const length = 2 * Math.PI * this.ring.r;
    return (p / 100 * length).toFixed(1) + ' ' + length.toFixed(1);
  }

  /* ------------------------------------------------------------- analytics */

  /** The moment the action card speaks for: the waiting choice, else the module's latest moment that has a card. */
  private cardMoment(): CementingMoment | null {
    if (this.action && this.action.moment) { return this.action.moment; }
    const moments = this.category ? this.category.moments || [] : [];
    const last = moments.length ? moments[moments.length - 1] : null;
    return last && last.moment && CARD_MOMENTS.indexOf(last.moment) > -1 ? last : null;
  }

  /** L1 Cementing_Moment_Shown: once per moment per student, not on every render (the status reloads after every choice and quiz). */
  private trackMomentShown() {
    const moment = this.cardMoment();
    const key = moment && moment.quizId ? moment.quizId + ':' + moment.moment : null;
    this.forgetFinishedModule(key);
    if (!moment || !key || !this.action) { this.shownMoment = null; return; }
    if (!this.shownMoment || this.shownMoment.quizId !== moment.quizId) { this.shownAt = Date.now(); }
    this.shownMoment = moment;
    if (!this.markMomentShown(key)) { return; }
    const a = this.action;
    const buttons = [a.primary, a.secondary].concat(a.links || []).filter(b => !!b).map(b => b.action);
    this.events.track('Cementing_Moment_Shown', {
      ...this.moduleProperties(moment),
      Moment: moment.moment,
      Moment_Quiz_Id: moment.quizId,
      Buttons_Shown: buttons.join(','),
      Recommended_Button: a.primary ? a.primary.action : null,
      // The card has no message catalog id yet (handoff open question): its kind and title stand in.
      Card_Kind: a.kind,
      Card_Title: a.title,
      Choice_Already_Made: !!moment.choice || this.localChoice === 'KEEP_GOING',
      Screen: this.screenName()
    });
  }

  /** L2 Cementing_Moment_Button_Clicked: every click on a moment card's buttons. */
  private trackButton(action: ActionName) {
    const moment = this.shownMoment;
    if (!moment || !this.action) { return; }
    this.events.track('Cementing_Moment_Button_Clicked', {
      ...this.moduleProperties(moment),
      Moment: moment.moment,
      Moment_Quiz_Id: moment.quizId,
      Button: action,
      Was_Recommended: !!this.action.primary && this.action.primary.action === action,
      Card_Kind: this.action.kind,
      Card_Title: this.action.title,
      Seconds_On_Card: this.shownAt ? Math.round((Date.now() - this.shownAt) / 1000) : null,
      Screen: this.screenName()
    });
  }

  /** The module set the server events carry, from the status the panel shows. */
  private moduleProperties(moment: CementingMoment): { [key: string]: any } {
    const c = this.category;
    const s = c.summary;
    const r = c.rules;
    return {
      Sub_Section: this.subject,
      Difficulty_Level: c.difficulty === 'HARD' ? 'Hard' : 'Medium',
      Category_Key: c.categoryKey,
      Cementing_Flow: this.status && this.status.mode ? this.status.mode : null,
      Quiz_Id: moment.quizId,
      Quiz_Position: moment.quizNumber,
      Checkin_Number: moment.moment && moment.moment.indexOf('CHECKIN_') === 0
        ? (c.moments || []).filter(m => m.moment && m.moment.indexOf('CHECKIN_') === 0 && m.quizNumber <= moment.quizNumber).length : null,
      Counted_Quizzes: s.countedQuizzes,
      Quizzes_Left: s.quizzesLeft,
      Module_State: s.status,
      Best_Level: s.bestLevel,
      Threshold_Pct: r.thresholdPct,
      Mastery_Pct: r.masteryTarget ? r.masteryTarget.percent : null,
      Mastery_Target: r.masteryTarget ? r.masteryTarget.score : null,
      L20_Max: r.l20Max
    };
  }

  private screenName(): string {
    return (window.location.href.indexOf('/journey') > -1 ? 'journey' : 'module') + ':' + this.view;
  }

  /** Remembers a moment as reported; false when it already was. Storage blocked: report it (a repeat beats a gap). */
  private markMomentShown(key: string): boolean {
    try {
      const raw = localStorage.getItem(SHOWN_MOMENTS_KEY);
      const sent: string[] = raw ? JSON.parse(raw) : [];
      if (sent.indexOf(key) > -1) { return false; }
      sent.push(key);
      localStorage.setItem(SHOWN_MOMENTS_KEY, JSON.stringify(sent.slice(-SHOWN_MOMENTS_MAX)));
    } catch (e) {
      // storage blocked or unreadable: the moment is reported anyway
      console.warn('[cementing-panel] could not read or store reported moments:', e);
    }
    return true;
  }

  /**
   * A finished module (Done or Not needed) shows no new moments: forget its reported moments, except the one
   * its card still shows (keep), so that card is not reported again on the next render.
   */
  private forgetFinishedModule(keep: string | null) {
    const c = this.category;
    if (!c || c.summary.open) { return; }
    const drop = (c.moments || []).filter(m => !!m.quizId).map(m => m.quizId + ':' + m.moment).filter(k => k !== keep);
    if (!drop.length) { return; }
    try {
      const raw = localStorage.getItem(SHOWN_MOMENTS_KEY);
      const sent: string[] = raw ? JSON.parse(raw) : [];
      const left = sent.filter(k => drop.indexOf(k) < 0);
      if (left.length !== sent.length) { localStorage.setItem(SHOWN_MOMENTS_KEY, JSON.stringify(left)); }
    } catch (e) {
      // storage blocked or unreadable: nothing to forget
      console.warn('[cementing-panel] could not clear reported moments:', e);
    }
  }

  /* --------------------------------------------------------- server choice */

  /**
   * "Keep going" when the server holds it on the waiting moment (it still offers "Change my choice"
   * until the next quiz starts), else nothing chosen yet.
   */
  private restoreChoice(): LocalChoice {
    const c = this.category;
    if (!c || c.summary.waitingQuizNumber === null || c.summary.waitingQuizNumber === undefined) { return null; }
    const waiting = (c.moments || []).find(m => m.quizNumber === c.summary.waitingQuizNumber);
    return waiting && waiting.choice === 'KEEP_GOING' ? 'KEEP_GOING' : null;
  }

  trackGroup(index: number, group: PlanGroup) { return group.label + ':' + (group.tiles.length ? group.tiles[0].quizNumber : index); }
  trackTile(index: number, tile: PlanTile) { return tile.quizNumber; }
  trackRow(index: number, row: TableRow) { return row.kind + ':' + row.quizNumber + ':' + index; }
  trackBar(index: number, bar: ChartBar) { return bar.column.quizNumber; }
}
